import { FC, useRef, useState } from 'react'

import styled from 'styled-components'

import CheckIcon from 'src/Assets/Svg/common/check-mark.svg?react'
import { MatchedCuratorsSortBy } from 'src/Redux/matchedCuratorsApi'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'

interface SortingMatchedCuratorsProps {
  value: MatchedCuratorsSortBy
  onChange: (value: MatchedCuratorsSortBy) => void
  onHide: () => void
}

const SORT_OPTIONS: { value: MatchedCuratorsSortBy; label: string }[] = [
  { value: MatchedCuratorsSortBy.BEST_MATCH, label: 'Best match' },
  { value: MatchedCuratorsSortBy.LOWEST_PRICE, label: 'Lowest price' },
  { value: MatchedCuratorsSortBy.HIGHEST_PRICE, label: 'Highest price' },
  { value: MatchedCuratorsSortBy.RECENT_ACTIVITY, label: 'Recent activity' },
]

const Wrapper = styled.div`
  display: flex;
  width: 240px;
  padding: 8px 8px 16px 8px;
  flex-direction: column;
  align-items: flex-start;
`

const Text = styled.div`
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const Item = styled.div<{ $isChecked?: boolean; $isDisabled?: boolean }>`
  display: flex;
  height: 40px;
  padding: 0px 16px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  cursor: pointer;
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
  border-radius: 8px;
  border: 1px solid transparent;
  background-color: transparent;
  outline: none;

  svg {
    color: ${({ $isChecked, $isDisabled, theme }) => {
      if ($isDisabled) {
        return theme.color.lightGrey
      }
      if ($isChecked) {
        return theme.color.brandGreen
      }
      return theme.color.darkGrey
    }};
  }

  ${Text} {
    color: ${({ $isDisabled, theme }) =>
      $isDisabled ? theme.color.lightGrey : theme.color.darkGrey};
  }

  &:hover {
    background-color: ${({ theme }) => theme.color.hoverWhite};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.color.brandGreen};
    background-color: ${({ theme }) => theme.color.hoverWhite};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.white};
    border-color: transparent;

    svg {
      color: ${({ theme }) => theme.color.slateGrey};
    }

    ${Text} {
      color: ${({ theme }) => theme.color.slateGrey};
    }
  }
`

const ApplyButton = styled(PrimaryDottedButton)`
  width: 100%;
  padding: 8px 0;
`

export const MATCHED_CURATORS_SORT_LABELS: Record<
  MatchedCuratorsSortBy,
  string
> = {
  [MatchedCuratorsSortBy.BEST_MATCH]: 'Best match',
  [MatchedCuratorsSortBy.LOWEST_PRICE]: 'Lowest price',
  [MatchedCuratorsSortBy.HIGHEST_PRICE]: 'Highest price',
  [MatchedCuratorsSortBy.RECENT_ACTIVITY]: 'Recent activity',
}

export const SortingMatchedCurators: FC<SortingMatchedCuratorsProps> = ({
  value,
  onChange,
  onHide,
}) => {
  const [pendingValue, setPendingValue] = useState(value)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    optionValue: MatchedCuratorsSortBy,
    index: number,
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setPendingValue(optionValue)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const items =
        wrapperRef.current?.querySelectorAll<HTMLElement>('[role="option"]')
      items?.[(index + 1) % SORT_OPTIONS.length]?.focus()
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      const items =
        wrapperRef.current?.querySelectorAll<HTMLElement>('[role="option"]')
      items?.[(index - 1 + SORT_OPTIONS.length) % SORT_OPTIONS.length]?.focus()
    }
  }

  const handleApply = (): void => {
    onChange(pendingValue)
    onHide()
  }

  return (
    <Wrapper ref={wrapperRef}>
      {SORT_OPTIONS.map((option, index) => (
        <Item
          key={option.value}
          role='option'
          tabIndex={0}
          aria-selected={pendingValue === option.value}
          $isChecked={pendingValue === option.value}
          onClick={() => setPendingValue(option.value)}
          onKeyDown={(e) => handleKeyDown(e, option.value, index)}
        >
          <Text>{option.label}</Text>
          {pendingValue === option.value && <CheckIcon />}
        </Item>
      ))}
      <ApplyButton onClick={handleApply}>Apply</ApplyButton>
    </Wrapper>
  )
}
